import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MediaUploader from '../../components/dashboard/MediaUploader';
import { Save, ArrowLeft, Trash2 } from 'lucide-react';

const SetEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = !id || id === 'new';

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        status: 'draft', // draft, submitted, published
        mediaItems: [],
        coverMedia: null,
        releaseDate: '',
        btsContent: []
    });
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);

    const fetchSet = React.useCallback(async (setId) => {
        const token = localStorage.getItem('paradise_token');
        try {
            const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000';
            const res = await fetch(`${API_BASE}/api/sets/${setId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setFormData({
                    title: data.title || '',
                    description: data.description || '',
                    status: data.status,
                    mediaItems: data.mediaItems || [],
                    coverMedia: data.coverMedia || null,
                    releaseDate: data.releaseDate || '',
                    btsContent: data.btsContent || []
                });
            } else {
                console.error("Failed to fetch set");
                navigate('/creator/sets');
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        if (!isNew) {
            fetchSet(id);
        }
    }, [id, isNew, fetchSet]);

    const handleSave = async (targetStatus = null) => {
        setSaving(true);
        const token = localStorage.getItem('paradise_token');
        const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000';

        const payload = { ...formData };
        if (targetStatus) payload.status = targetStatus;

        try {
            let res;
            if (isNew) {
                res = await fetch(`${API_BASE}/api/sets`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });
            } else {
                res = await fetch(`${API_BASE}/api/sets/${id}`, {
                    method: 'PATCH',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });
            }

            if (res.ok) {
                const data = await res.json();
                if (isNew) {
                    navigate(`/creator/sets/${data._id}`);
                } else {
                    // Refresh local?
                    alert('Saved successfully');
                }
            } else {
                alert('Failed to save');
            }
        } catch (e) {
            console.error(e);
            alert('Error saving set');
        } finally {
            setSaving(false);
        }
    };

    const handleMediaUpload = (mediaItem) => {
        // Add to mediaItems
        // If it's the first image and no cover, set as cover
        // Transform to Schema Match
        // Schema expects: type, cloudflareImageId (if image), variants (if image), streamUid (if video), etc.
        const newItem = {
            _key: Math.random().toString(36).substring(7), // Necessary for arrays in Sanity
            type: mediaItem.type,
            // Add other logical fields based on type
        };

        if (mediaItem.type === 'image') {
            newItem.cloudflareImageId = mediaItem.cloudflareId;
            // Use returned variants if available (avoids needing Account Hash in env)
            newItem.variants = mediaItem.variants && mediaItem.variants.length > 0
                ? mediaItem.variants
                : [`https://imagedelivery.net/${process.env.REACT_APP_CLOUDFLARE_ACCOUNT_HASH}/${mediaItem.cloudflareId}/public`];
            // Store raw ID for UI previews too if needed, but schema uses 'cloudflareImageId'
            // UI uses 'id' currently, let's keep it for local preview or map it
            newItem.id = mediaItem.cloudflareId;
        } else {
            newItem.streamUid = mediaItem.cloudflareId;
        }

        const items = [...formData.mediaItems, newItem];
        let cover = formData.coverMedia;

        if (!cover && newItem.type === 'image') {
            cover = newItem;
        }

        setFormData(prev => ({
            ...prev,
            mediaItems: items,
            coverMedia: cover
        }));
    };

    const handleRemoveMedia = (index) => {
        const items = [...formData.mediaItems];
        items.splice(index, 1);
        setFormData(prev => ({
            ...prev,
            mediaItems: items
        }));
    };

    const handleBtsUpload = (mediaItem) => {
        // Similar transform as mediaItems
        const newItem = {
            _key: Math.random().toString(36).substring(7),
            type: mediaItem.type,
        };

        if (mediaItem.type === 'image') {
            newItem.cloudflareImageId = mediaItem.cloudflareId;
            newItem.variants = mediaItem.variants && mediaItem.variants.length > 0
                ? mediaItem.variants
                : [`https://imagedelivery.net/${process.env.REACT_APP_CLOUDFLARE_ACCOUNT_HASH}/${mediaItem.cloudflareId}/public`];
            newItem.id = mediaItem.cloudflareId;
        } else {
            newItem.streamUid = mediaItem.cloudflareId;
        }

        setFormData(prev => ({
            ...prev,
            btsContent: [...prev.btsContent, newItem]
        }));
    };

    const handleRemoveBts = (index) => {
        const items = [...formData.btsContent];
        items.splice(index, 1);
        setFormData(prev => ({ ...prev, btsContent: items }));
    };

    const handleCoverUpload = (mediaItem) => {
        // Transform for consistency (though coverMedia is single object)
        const newItem = {
            _key: Math.random().toString(36).substring(7),
            type: mediaItem.type,
        };

        if (mediaItem.type === 'image') {
            newItem.cloudflareImageId = mediaItem.cloudflareId;
            newItem.variants = mediaItem.variants && mediaItem.variants.length > 0
                ? mediaItem.variants
                : [`https://imagedelivery.net/${process.env.REACT_APP_CLOUDFLARE_ACCOUNT_HASH}/${mediaItem.cloudflareId}/public`];
            newItem.id = mediaItem.cloudflareId;
        }
        // Videos unlikely to be cover, but support if schema allows (usually image)

        setFormData(prev => ({ ...prev, coverMedia: newItem }));
    };

    const handleRemoveCover = () => {
        setFormData(prev => ({ ...prev, coverMedia: null }));
    };

    if (loading) return <div>Loading Editor...</div>;

    return (
        <div className="max-w-4xl mx-auto pb-20">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center space-x-4">
                    <button onClick={() => navigate('/creator/sets')} className="p-2 hover:bg-gray-200 rounded-full">
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold">{isNew ? 'Create New Set' : formData.title}</h1>
                        <span className={`text-xs uppercase font-bold tracking-widest px-2 py-1 rounded ${formData.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                            {formData.status}
                        </span>
                    </div>
                </div>
                <div className="flex items-center space-x-3">
                    <button
                        onClick={() => handleSave('draft')}
                        disabled={saving}
                        className="flex items-center space-x-2 bg-white border border-gray-300 px-4 py-2 rounded-md text-sm font-bold hover:bg-gray-50 transition-colors"
                    >
                        <Save size={16} />
                        <span>SAVE DRAFT</span>
                    </button>
                    <button
                        onClick={() => handleSave('submitted')}
                        disabled={saving}
                        className="bg-black text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-gray-800 transition-colors"
                    >
                        SUBMIT FOR REVIEW
                    </button>
                </div>
            </div>

            {/* Form */}
            <div className="space-y-8">
                {/* Details */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 space-y-4">
                    <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400">Set Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wide mb-1">Title</label>
                            <input
                                type="text"
                                className="w-full bg-[#f9f9f9] p-3 border border-gray-300 rounded-sm text-lg font-bold"
                                value={formData.title}
                                onChange={e => setFormData({ ...formData, title: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wide mb-1">Release Date</label>
                            <input
                                type="date"
                                className="w-full bg-[#f9f9f9] p-3 border border-gray-300 rounded-sm font-mono"
                                value={formData.releaseDate}
                                onChange={e => setFormData({ ...formData, releaseDate: e.target.value })}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wide mb-1">Description</label>
                        <textarea
                            rows="4"
                            className="w-full bg-[#f9f9f9] p-3 border border-gray-300 rounded-sm"
                            value={formData.description}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                        ></textarea>
                    </div>
                </div>

            </div>

            {/* Cover Image Section */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400">Cover Image</h3>
                <p className="text-xs text-gray-500">This image will be used for the set preview card.</p>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {formData.coverMedia ? (
                        <div className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group border border-green-500 border-2">
                            <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-500 bg-gray-50">
                                {formData.coverMedia.type === 'image' && (formData.coverMedia.variants?.[0] || formData.coverMedia.id) ? (
                                    <img src={formData.coverMedia.variants?.[0] || `https://imagedelivery.net/${process.env.REACT_APP_CLOUDFLARE_ACCOUNT_HASH}/${formData.coverMedia.id}/public`} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <span>COVER</span>
                                )}
                            </div>
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={handleRemoveCover}
                                    className="bg-white p-1 rounded-full text-red-500 shadow-md hover:bg-red-50"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                            <div className="absolute bottom-2 left-2 bg-green-500 text-white text-[10px] px-1 rounded uppercase font-bold">
                                COVER
                            </div>
                        </div>
                    ) : (
                        <div className="aspect-square">
                            <MediaUploader onUploadComplete={handleCoverUpload} />
                        </div>
                    )}
                </div>
            </div>

            {/* Main Content Media */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400">Content</h3>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.mediaItems.map((item, idx) => (
                        <div key={idx} className={`relative aspect-square bg-gray-100 rounded-lg overflow-hidden group border ${formData.coverMedia?.id === item.id ? 'border-green-500 border-2' : 'border-gray-200'}`}>
                            <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-500 bg-gray-50">
                                {item.type === 'image' && (item.variants?.[0] || item.id) ? (
                                    <img src={item.variants?.[0] || `https://imagedelivery.net/${process.env.REACT_APP_CLOUDFLARE_ACCOUNT_HASH}/${item.id}/public`} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <span>{item.type === 'video' ? 'VIDEO' : 'IMAGE'}</span>
                                )}
                            </div>
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-2">
                                {item.type === 'image' && (
                                    <button
                                        title="Set as Cover"
                                        onClick={() => setFormData({ ...formData, coverMedia: item })}
                                        className="bg-white p-1 rounded-full text-green-600 shadow-md hover:bg-green-50"
                                    >
                                        <div className="w-3 h-3 bg-green-500 rounded-full" />
                                    </button>
                                )}
                                <button
                                    onClick={() => handleRemoveMedia(idx)}
                                    className="bg-white p-1 rounded-full text-red-500 shadow-md hover:bg-red-50"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                            {/* Badge */}
                            <div className="absolute bottom-2 left-2 flex flex-col items-start space-y-1">
                                <div className="bg-black/50 backdrop-blur text-white text-[10px] px-1 rounded uppercase font-bold">
                                    {item.type}
                                </div>
                                {formData.coverMedia?.id === item.id && (
                                    <div className="bg-green-500 text-white text-[10px] px-1 rounded uppercase font-bold">
                                        COVER
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    {/* Uploader Card */}
                    <div className="aspect-square">
                        <MediaUploader onUploadComplete={handleMediaUpload} />
                    </div>
                </div>
            </div>

            {/* Vault Content */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400">Vault Content (BTS)</h3>
                <p className="text-xs text-gray-500">Exclusive Behind-the-Scenes content for Executive members.</p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.btsContent.map((item, idx) => (
                        <div key={idx} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group border border-gray-200">
                            <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-500 bg-gray-50">
                                {item.type === 'image' && (item.variants?.[0] || item.id) ? (
                                    <img src={item.variants?.[0] || `https://imagedelivery.net/${process.env.REACT_APP_CLOUDFLARE_ACCOUNT_HASH}/${item.id}/public`} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <span>{item.type === 'video' ? 'VIDEO' : 'IMAGE'}</span>
                                )}
                            </div>
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={() => handleRemoveBts(idx)}
                                    className="bg-white p-1 rounded-full text-red-500 shadow-md hover:bg-red-50"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                            <div className="absolute bottom-2 left-2 bg-purple-600 text-white text-[10px] px-1 rounded uppercase font-bold">
                                VAULT
                            </div>
                        </div>
                    ))}

                    <div className="aspect-square">
                        <MediaUploader onUploadComplete={handleBtsUpload} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SetEditor;
